import { Link } from "wouter";
import { formatMoney, formatDateTime } from "@/lib/format";
import { Calendar, MapPin, Users, Trophy, CheckCircle2, Clock } from "lucide-react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Tournament } from "@workspace/api-client-react";
import { storedImageUrl } from "@/lib/upload";

interface TournamentCardProps {
  tournament: Pick<
    Tournament,
    "id" | "name" | "leagueName" | "date" | "buyIn" | "entries" | "prizePool" | "status"
  > &
    Partial<Pick<Tournament, "venueName" | "venueCity" | "guaranteedPrizePool" | "imageUrl" | "capacity" | "registeredCount" | "waitlistCount">>;
  isRegistered?: boolean;
}

export function TournamentCard({ tournament, isRegistered = false }: TournamentCardProps) {
  const isUpcoming = tournament.status === "upcoming";
  const isActive = tournament.status === "active";
  const isCompleted = tournament.status === "completed";
  const guaranteed = tournament.guaranteedPrizePool ?? 0;
  const heroUrl = storedImageUrl(tournament.imageUrl ?? null);

  return (
    <Card className="group relative overflow-hidden bg-card border-card-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
      <div className={`absolute top-0 left-0 w-full h-1 z-10 ${
        isUpcoming ? "bg-primary" : isActive ? "bg-accent" : "bg-muted"
      }`} />
      {heroUrl && (
        <div className="aspect-[16/9] w-full overflow-hidden bg-muted">
          <img
            src={heroUrl}
            alt={tournament.name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}

      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant={isUpcoming ? "default" : isActive ? "secondary" : "outline"} className={isActive ? 'bg-accent text-accent-foreground' : ''}>
                {tournament.status.toUpperCase()}
              </Badge>
              {isRegistered && (
                <Badge
                  variant="outline"
                  className="border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400 gap-1"
                  data-testid={`badge-rsvpd-${tournament.id}`}
                >
                  <CheckCircle2 className="h-3 w-3" /> RSVP'd
                </Badge>
              )}
            </div>
            <h3 className="text-xl font-bold text-foreground leading-tight">{tournament.name}</h3>
            <p className="text-sm text-primary font-medium mt-1">{tournament.leagueName}</p>
          </div>
        </div>

        <div className="space-y-3 mt-6">
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="mr-2 h-4 w-4 text-primary/70" />
            {formatDateTime(tournament.date)}
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <MapPin className="mr-2 h-4 w-4 text-primary/70" />
            {tournament.venueName ? `${tournament.venueName}${tournament.venueCity ? `, ${tournament.venueCity}` : ''}` : "TBA"}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border/50">
            <div>
              <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">Buy-in</p>
              <p className="font-semibold text-foreground">{formatMoney(tournament.buyIn)}</p>
            </div>
            {guaranteed > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider flex items-center gap-1">
                  <Trophy className="h-3 w-3 text-primary" /> GTD
                </p>
                <p className="font-semibold text-primary">{formatMoney(guaranteed)}</p>
              </div>
            )}
            {isCompleted && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">Prize Pool</p>
                <p className="font-semibold text-primary">{formatMoney(tournament.prizePool)}</p>
              </div>
            )}
            <div className={isCompleted && guaranteed > 0 ? "col-span-2" : ""}>
              <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider flex items-center gap-1">
                <Users className="h-3 w-3" /> {tournament.capacity != null ? "Seats" : "Entries"}
              </p>
              {tournament.capacity != null ? (
                <p className="font-semibold text-foreground tabular-nums" data-testid={`seats-${tournament.id}`}>
                  {(tournament.registeredCount ?? 0)} / {tournament.capacity}
                </p>
              ) : (
                <p className="font-semibold text-foreground">{tournament.entries}</p>
              )}
            </div>
          </div>

          {tournament.capacity != null && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {(tournament.registeredCount ?? 0) >= tournament.capacity && (
                <Badge
                  variant="outline"
                  className="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                  data-testid={`badge-full-${tournament.id}`}
                >
                  Full
                </Badge>
              )}
              <Badge
                variant="outline"
                className="border-muted-foreground/30 bg-muted/40 text-muted-foreground gap-1"
                data-testid={`badge-waitlist-${tournament.id}`}
              >
                <Clock className="h-3 w-3" /> Waitlist: {tournament.waitlistCount ?? 0}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="p-6 pt-0">
        <Link href={`/tournaments/${tournament.id}`} className="w-full">
          <Button variant={isUpcoming ? "default" : "outline"} className="w-full font-medium">
            {isUpcoming ? "Register / Info" : isCompleted ? "View Results" : "View Info"}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
